/**
 * Mobile Handover Note Page
 * Allows creating handover notes offline with auto-sync
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Save, ClipboardList, Clock, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';

const PRIORITY_LEVELS = [
  { value: 'LOW', label: 'Low', icon: Info, color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  { value: 'NORMAL', label: 'Normal', icon: CheckCircle, color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  { value: 'HIGH', label: 'High', icon: AlertCircle, color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { value: 'URGENT', label: 'Urgent', icon: AlertCircle, color: 'bg-red-500/10 text-red-600 border-red-500/30' },
];

export default function MobileHandoverPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shiftId = searchParams.get('shiftId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState({
    shiftId: shiftId || '',
    content: '',
    priority: 'NORMAL',
    participantIds: [],
  });

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants
  useEffect(() => {
    if (online) {
      api.get('/participants', { params: { pageSize: 100 } })
        .then(res => setParticipants(res.data.items || []))
        .catch(() => {});
    }
  }, [online]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleParticipant = (id) => {
    setForm(prev => ({
      ...prev,
      participantIds: prev.participantIds.includes(id)
        ? prev.participantIds.filter(p => p !== id)
        : [...prev.participantIds, id]
    }));
  };

  const saveDraft = async () => {
    if (!form.content.trim()) {
      toast.error('Please enter handover content');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      await queueOfflineAction(ACTION_TYPES.HANDOVER_NOTE, {
        shiftId: form.shiftId,
        content: form.content,
        priority: form.priority,
        participantIds: form.participantIds,
        timestamp: new Date().toISOString(),
      });

      toast.success('Handover note saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Handover synced successfully');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save handover');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-handover-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-bold">Handover Note</h1>
            </div>
            <p className="text-xs text-muted-foreground">Pass information to the next shift</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Priority Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Priority</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-2 gap-2">
              {PRIORITY_LEVELS.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('priority', value)}
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    form.priority === value 
                      ? `${color} border-current` 
                      : 'bg-muted/50 border-transparent hover:bg-muted'
                  }`}
                  data-testid={`priority-${value.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Handover Content */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Handover Information *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="content"
              placeholder="What does the next staff member need to know?&#10;&#10;Include:&#10;- Important updates&#10;- Tasks to follow up&#10;- Participant status changes&#10;- Any concerns"
              value={form.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={8}
              className="resize-none"
              data-testid="handover-content"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {form.content.length} characters
            </p>
          </CardContent>
        </Card>

        {/* Related Participants */}
        {participants.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Related Participants (Optional)</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {participants.map(p => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={form.participantIds.includes(p.id)}
                      onCheckedChange={() => toggleParticipant(p.id)}
                      data-testid={`participant-${p.id}`}
                    />
                    <span className="text-sm">{p.first_name} {p.last_name}</span>
                  </label>
                ))}
              </div>
              {form.participantIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {form.participantIds.length} participant(s) selected
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1"
            onClick={saveDraft}
            disabled={saving || !form.content.trim()}
            data-testid="save-handover-btn"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {online ? 'Save & Sync' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
